<template>
  <div :class="`${blockName} ${blockClasses}`">
    <div :class="`${blockName}__element ${blockName}__element--play ${playButtonClasses}`">
      <div
        v-if="audioUrl"
        @click="togglePlayPause"
        :class="{
          [`${blockName}__play`]: true,
          [`${blockName}__pause`]: isPlaying
        }"
      />
    </div>

    <div :class="`${blockName}__element ${blockName}__element--time`" v-if="hasTime">
      <div :class="`${blockName}__remaining-time`">
        {{ prettyCurrentTime }}
      </div>
    </div>

    <div :class="`${blockName}__element ${blockName}__element--progress`">
      <ranger
        :class="`${blockName}__progress`"
        @change="onProgressChanged"
        :value="progressPercent"
        :has-thumb="hasProgressThumb"
      />
    </div>

    <div :class="`${blockName}__element ${blockName}__element--total`">
      <div v-if="totalTime !== null" :class="`${blockName}__total-time`">
        {{ prettyTotalTime }}
      </div>
    </div>
    <template v-if="hasVolume">
      <div :class="`${blockName}__element ${blockName}__element--sound`">
        <div
          @click="toggleMute"
          :class="{
            [`${blockName}__sound`]: true,
            [`${blockName}__sound--mute`]: isMuted
          }"
        />
      </div>

      <div :class="`${blockName}__element ${blockName}__element--volume`">
        <ranger :class="`${blockName}__volume`" @change="onVolumeChanged" :value="volumePercent" />
      </div>
    </template>
  </div>
</template>

<script>
import events from "@/bl-events"
import Ranger from "@/shared/audio-player/ranger.vue"
import createAudio from "@/utils/audio-factory"
import prettifyTime from "@/utils/prettify-time"
import bemBlockMofifiers from "@/utils/bem-block-modifiers"

export default {
  name: "AudioPlayer",
  components: { Ranger },
  props: {
    audioUrl: { type: String, default: null },
    blockName: { type: String, default: null },
    hasVolume: { type: Boolean, default: true },
    hasProgressThumb: { type: Boolean, default: true },
    hasTime: { type: Boolean, default: true },
    resetOnEnd: { type: Boolean, default: false },
    eventName: { type: String, default: "body-20190328-audio_player" },
    eventPayload: { type: String, default: null },
    theme: {
      type: String,
      default: "light"
    },
    playButtonClasses: {
      type: String,
      default: ""
    }
  },

  data() {
    return {
      audio: createAudio(),
      isPaused: true,
      currentTime: null,
      totalTime: null,
      progressPercent: 0,
      volumePercent: 100,
      unmutedVolumePercent: 100
    }
  },

  computed: {
    isMuted() {
      return this.volumePercent === 0
    },

    isPlaying() {
      return !this.isPaused
    },

    prettyCurrentTime() {
      return prettifyTime(this.currentTime)
    },

    prettyTotalTime() {
      return prettifyTime(this.totalTime)
    },

    blockClasses() {
      return bemBlockMofifiers(this.blockName, this.theme)
    }
  },

  watch: {
    audioUrl(newValue) {
      this.audio.src = newValue
    },

    currentTime(newValue) {
      const newPercentage = (newValue / this.totalTime) * 100
      this.progressPercent = parseInt(newPercentage)
    },

    volumePercent(newValue) {
      this.audio.volume = newValue / 100.0
    }
  },

  mounted() {
    this.audio.preload = "metadata"

    if (this.audioUrl) {
      this.audio.src = this.audioUrl
    }

    this.audio.onloadedmetadata = this.onAudioCanPlay
    this.audio.ontimeupdate = this.onAudioTimeUpdate
    this.audio.onended = this.onAudioEnded
  },

  methods: {
    togglePlayPause() {
      if (this.isPaused) {
        this.$emit("playPauseClick", "play")
        this.play()
      } else {
        this.pause()
        this.$emit("playPauseClick", "pause")
      }
    },

    toggleMute() {
      if (this.isMuted) {
        this.volumePercent = this.unmutedVolumePercent
      } else {
        this.unmutedVolumePercent = this.volumePercent
        this.volumePercent = 0
      }
    },

    play() {
      try {
        this.audio.play()
        this.$emit("play")
        this.track("1-play")
        this.isPaused = false
      } catch (e) {
        if (this.$store) {
          this.$store.dispatch("toaster/push", {
            id: `audioplayer-failure-${Date.now()}`,
            message: this.$t("playerFailed"),
            type: "error"
          })
        }
      }
    },

    pause() {
      if (this.isPaused) {
        return
      }
      this.audio.pause()
      this.$emit("pause")
      this.track("1-pause")
      this.isPaused = true
    },

    goTo(value) {
      this.audio.currentTime = value
      this.track("1-skip", value)
    },

    onProgressChanged(value, lastValue) {
      const time = (value / 100.0) * this.totalTime
      const prevTime = (lastValue / 100.0) * this.totalTime

      this.$emit("playbackPositionChange", time, prevTime)

      this.goTo(time)
    },

    onVolumeChanged(value) {
      this.volumePercent = value
      this.track("1-volume", value)
    },

    onAudioEnded() {
      this.$emit("ended")

      if (this.resetOnEnd) {
        this.goTo(0)
        this.togglePlayPause()
      }
    },

    // Because of restrictions around autoplay of video and sounds Safari does
    // not fire the `oncanplay` callback like other browsers and therefore we
    // had a bug (https://app.asana.com/0/1120615199861443/1124121253013408)
    // that caused the next blink not to start automatically.
    // Using the `onloadedmetadata` callback is the closest we can get.
    // See also this discussion: https://stackoverflow.com/questions/50051639/javascript-html5-video-event-canplay-not-firing-on-safari
    onAudioCanPlay() {
      this.currentTime = parseInt(this.audio.currentTime)
      this.totalTime = parseInt(this.audio.duration)

      if (this.isPlaying) {
        this.play()
      }
    },

    onAudioTimeUpdate() {
      this.currentTime = parseInt(this.audio.currentTime)
    },

    track(action, value) {
      const args = [this.eventName, action, this.eventPayload || this.audioUrl]
      if (typeof value !== "undefined") {
        args.push(value)
      }
      events.track(...args)
    }
  },
  i18n: {
    messages: {
      en: {
        playerFailed:
          "Playing audio failed. Please check your browser settings for permission to play audio from this page and your internet connection."
      },
      de: {
        playerFailed:
          "Audiowiedergabe fehlgeschlagen. Bitte prüfe deine Browser Einstellungen, sodass diese Webseite Audio abspielen darf. Stelle auch sicher, dass du eine gute Internetverbindung hast."
      }
    }
  }
}
</script>
